import { ref, computed } from 'vue';

import axios from 'axios';
import { defineStore } from 'pinia';

import { useRouter } from 'vue-router';

import Consts from '@/api/Consts';


const PROFILE = 'profile';
const VERSION = 1;

const isEmpty = value => value == null || value.length === 0;

const readProfile = () => {
	const profile = localStorage.getItem(PROFILE);
	let token = '';
	let username = '';
	let role = '';
	let version = VERSION;
	if (profile) {
		const data = JSON.parse(profile);
		if (data.version === VERSION) {
			token = data.token;
			username = data.username;
			role = data.role;
		}
	}
	return {
		token,
		username,
		role,
		version,
	}
}

const writeProfile = (data) => {
	const profile = readProfile();
	Object.assign(profile, data);
	profile.version = VERSION;
	localStorage.setItem(PROFILE, JSON.stringify(profile));
}

const checkIsAuthenticated = (token, username, role) => {
	return !(isEmpty(token) || isEmpty(username) || isEmpty(role) || role == 'unauthorized');
}

const isMixerAdmin = role => role == 'mixer_admin';
const isRoverAdmin = role => role == 'rover_admin';
const isUnconfirmed = role => role == 'unconfirmed';

export const useUserStore = defineStore('user', () => {
	const profile = readProfile();
	const token = ref(profile.token);
	const username = ref(profile.username);
	const role = ref(profile.role);
	const isAuthenticated = computed(() => checkIsAuthenticated(token.value, username.value, role.value));
	const updateHeaders = () => {
		axios.defaults.headers.common['Authorization'] = isAuthenticated.value ? ('Token ' + token.value) : '';
	};
	updateHeaders();

	const menuItemsKeys = computed(() => {
		if (isUnconfirmed(role.value)) {
			return ['activation-required'];
		}
		let items = [];
		if (!isAuthenticated.value) {
			items.push('home');
		}
		items.push('rovers', 'stats', 'payments');
		if (isMixerAdmin(role.value)) {
			items.push('admin-stats', 'events');
		}
		items.push('tariffs');
		if (isAuthenticated.value) {
			items.push('account');
		} else {
			items.push('login', 'registration');
		}
		return items;
	});

	const menuItems = computed(() => menuItemsKeys.value.map(key => {
		if (key === 'account') {
			return { key, label: username.value };
		}
		return { key }
	}));

	const homePage = computed(() => {
		if (isAuthenticated.value) {
			return isUnconfirmed(role.value) ? 'activation-required' : 'rovers';
		}
		return 'home';
	});

	const roversTableColumns = computed(() => {
		let columns = ['label', 'user_id', 'display_status', 'geo_fencing', 'actions', 'tariff'];
		if (isMixerAdmin(role.value)) {
			columns.push('group_uid');
		}
		return Consts.ROVERS_TABLE_COLUMNS.filter(item => columns.includes(item.key));
	});

	const roversStatsTableColumns = computed(() => {
		let columns = ['label', 'duration_online', 'last_online_datetime', 'added_datetime'];
		if (isMixerAdmin(role.value)) {
			columns.push('group_uid', 'user_id');
		}
		return Consts.ROVERS_STATS_TABLE_COLUMNS.filter(item => columns.includes(item.dataIndex));
	});

	const paymentsTableColumns = computed(() => {
		let columns = ['uuid', 'status', 'amount', 'created_at', 'paid_at', 'description'];
		if (isMixerAdmin(role.value)) {
			columns.push('group_uid', 'username');
		}
		return Consts.PAYMENTS_TABLE_COLUMNS.filter(item => columns.includes(item.dataIndex));
	});

	const allowAddUpdateRover = computed(() => isRoverAdmin(role.value));
	const allowChangeTariff = computed(() => isRoverAdmin(role.value));
	const allowMakePayments = computed(() => isRoverAdmin(role.value));
	const allowShowPaymentOwner = computed(() => isMixerAdmin(role.value));

	const updateRole = roleValue => {
		role.value = roleValue;
		writeProfile({ role: roleValue });
		updateHeaders();
	};

	const login = data => {
		token.value = data.auth_token;
		username.value = data.username;
		writeProfile({
			token: data.auth_token,
			username: data.username,
		});
		updateRole(data.role);
	};
	const logout = () => {
		token.value = '';
		username.value = '';
		role.value = '';
		localStorage.removeItem(PROFILE);
		updateHeaders();
	};

	const router = useRouter();
	const redirectToLogin = () => router.push({ name: 'login' });

	return {
		token,
		username,
		role,
		menuItems,
		homePage,
		isAuthenticated,
		roversTableColumns,
		roversStatsTableColumns,
		paymentsTableColumns,
		allowAddUpdateRover,
		allowChangeTariff,
		allowMakePayments,
		allowShowPaymentOwner,

		login,
		logout,
		redirectToLogin,
		updateRole,
	};
});
